import {
  CustomerAction,
  AnnotationRecordStatus,
  HandId,
} from "@/entities/annotation";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@/shared/api/base-query";
import { RelativeCoordinate } from "../types";
import { OrderWithJourneys } from "@/features/annotation/lib/helpers";
import { lodApiInstance } from "./axios";
import { type OrderStatus } from "@/features/assignment/lib/types";
import {
  InfiniteQueryFilterParams,
  PaginatedQueryFilterParams,
} from "@/shared/types/filters";
import { GondolaChangeGroupsData } from "@/entities/gondola-change-groups";

const baseQuery = axiosBaseQuery(lodApiInstance);
const DEFAULT_PAGE_SIZE = 20;

export const lodApi = createApi({
  reducerPath: "lodApi",
  baseQuery,
  tagTypes: [
    "Order",
    "OrderDetails",
    "OrderStatus",
    "Journey",
    "JourneyTransactions",
    "TrackPersistences",
    "JourneyProximities",
    "ProximityTimeRanges",
    "Stages",
    "DashboardOrderHistories",
  ],
  endpoints: (builder) => ({
    getOrdersInfinite: builder.infiniteQuery<
      OrdersResponse,
      InfiniteQueryFilterParams,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (
          lastPage,
          allPages,
          lastPageParam,
          _allPageParams,
          queryArg
        ) => {
          const limit = queryArg.limit || DEFAULT_PAGE_SIZE;
          const itemsCount = limit * allPages.length;

          if (lastPage.count <= itemsCount) {
            return undefined;
          }

          return lastPageParam + 1;
        },
      },
      query: ({ queryArg, pageParam }) => {
        const { limit = DEFAULT_PAGE_SIZE, ...apiFilters } = queryArg;
        return {
          url: "orders",
          method: "GET",
          params: { ...apiFilters, page: pageParam, limit },
        };
      },
      providesTags: (result) => {
        if (!result) return [{ type: "Order", id: "INFINITE_LIST" }];

        const allOrders = result.pages.flatMap((page) => page.results);
        return [
          ...allOrders.map((order: Order) => ({
            type: "Order" as const,
            id: order.id,
          })),
          { type: "Order", id: "INFINITE_LIST" },
        ];
      },
    }),
    getOrdersPaginated: builder.query<
      OrdersResponse,
      PaginatedQueryFilterParams
    >({
      query: (params = {}) => ({ url: "orders", method: "GET", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map((order: Order) => ({
                type: "Order" as const,
                id: order.id,
              })),
              { type: "Order", id: "PAGINATED_LIST" },
            ]
          : [{ type: "Order", id: "PAGINATED_LIST" }],
    }),
    orderById: builder.query<OrderWithJourneysResponse, string>({
      query: (orderId) => ({ url: `/orders/${orderId}`, method: "GET" }),
      providesTags: (_result, _error, orderId) => [
        { type: "OrderDetails", id: orderId },
      ],
    }),

    updateOrderStatus: builder.mutation<
      { success: true },
      { orderId: string; status: OrderStatus }
    >({
      query: ({ orderId, status }) => ({
        url: `/orders/${orderId}/update_status`,
        method: "POST",
        data: { status },
      }),
      invalidatesTags: ["OrderStatus"],
    }),

    createJourney: builder.mutation<
      { id: string },
      { orderId: string; journey: JourneyEventRequest }
    >({
      query: ({ orderId, journey }) => ({
        url: `/orders/${orderId}/journeys`,
        method: "POST",
        data: journey,
      }),
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    createJourneys: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      { orderId: string; journeys: JourneyEventRequest[] }
    >({
      async queryFn(
        { orderId, journeys },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journey of journeys) {
          try {
            await fetchWithBQ({
              url: `/orders/${orderId}/journeys`,
              method: "POST",
              data: journey,
            });

            results.push({ success: true });
          } catch (error) {
            results.push({ error });
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    updateJourneys: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeys: Array<{ id: string; journey: JourneyEventRequest }>;
      }
    >({
      async queryFn(
        { orderId, journeys },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const item of journeys) {
          try {
            await fetchWithBQ({
              url: `/orders/${orderId}/journeys/${item.id}`,
              method: "PUT",
              data: item.journey,
            });

            results.push({ success: true });
          } catch (error) {
            results.push({ error });
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    deleteJourneys: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      { orderId: string; journeyIds: string[] }
    >({
      async queryFn(
        { orderId, journeyIds },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyId of journeyIds) {
          try {
            await fetchWithBQ({
              url: `/orders/${orderId}/journeys/${journeyId}`,
              method: "DELETE",
            });

            results.push({ success: true });
          } catch (error) {
            results.push({ error });
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    createJourneyTransactions: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyTransactionRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/transaction_annotations`,
                method: "POST",
                data: record,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyTransactions"],
    }),

    deleteJourneyTransactions: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyTransactionResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/transaction_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyTransactions"],
    }),

    createTrackPersistences: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: TrackPersistenceRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/track_persistence_annotations`,
                method: "POST",
                data: record,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["TrackPersistences"],
    }),

    deleteTrackPersistences: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: TrackPersistenceResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/track_persistence_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["TrackPersistences"],
    }),

    createJourneyProximities: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyProximityRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations`,
                method: "POST",
                data: record,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyProximities"],
    }),

    deleteJourneyProximities: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyProximityResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyProximities"],
    }),

    createProximityTimeRanges: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: ProximityTimeRangeRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            const {
              proximity_annotation_id: proximityAnnotationId,
              ...recordData
            } = record;
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations/${proximityAnnotationId}/proximity_range_annotations`,
                method: "POST",
                data: recordData,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["ProximityTimeRanges"],
    }),

    deleteProximityTimeRanges: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: ProximityTimeRangeResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;
          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations/${record.proximity_annotation_id}/proximity_range_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["ProximityTimeRanges"],
    }),
    getExportCounts: builder.query<
      ExportCountsResponse,
      ExportCountsQueryParams
    >({
      query: (params) => ({
        url: `/orders/export/count`,
        method: "GET",
        params,
      }),
    }),
    assignOrders: builder.mutation<AssignOrdersResponse, AssignOrdersVariables>(
      {
        query: (data) => ({ url: `/orders/assign`, method: "POST", data }),
        async onQueryStarted(
          { order_ids, annotator_id },
          { dispatch, queryFulfilled, getState }
        ) {
          const patchResults: any[] = [];

          try {
            const state = getState() as any;
            const ordersQueries = state.lodApi?.queries || {};

            Object.keys(ordersQueries).forEach((queryKey) => {
              if (queryKey.startsWith("getOrdersInfinite(")) {
                const queryData = ordersQueries[queryKey];
                const queryArgs = queryData?.originalArgs;

                if (queryArgs !== undefined && queryData?.data?.pages) {
                  const hasRelevantOrders = queryData.data.pages.some(
                    (page: OrdersResponse) =>
                      page.results.some((order: Order) =>
                        order_ids.includes(order.id)
                      )
                  );

                  if (hasRelevantOrders) {
                    const patchResult = dispatch(
                      lodApi.util.updateQueryData(
                        "getOrdersInfinite",
                        queryArgs,
                        (draft) => {
                          draft.pages.forEach((page) => {
                            page.results.forEach((order) => {
                              if (order_ids.includes(order.id)) {
                                order.annotator_id = annotator_id || "";
                              }
                            });
                          });
                        }
                      )
                    );
                    patchResults.push(patchResult);
                  }
                }
              }

              if (queryKey.startsWith("getOrdersPaginated(")) {
                const queryData = ordersQueries[queryKey];
                const queryArgs = queryData?.originalArgs;

                if (queryArgs !== undefined && queryData?.data?.results) {
                  const hasRelevantOrders = queryData.data.results.some(
                    (order: Order) => order_ids.includes(order.id)
                  );

                  if (hasRelevantOrders) {
                    const patchResult = dispatch(
                      lodApi.util.updateQueryData(
                        "getOrdersPaginated",
                        queryArgs,
                        (draft) => {
                          if (draft?.results) {
                            draft.results.forEach((order) => {
                              if (order_ids.includes(order.id)) {
                                order.annotator_id = annotator_id || "";
                              }
                            });
                          }
                        }
                      )
                    );
                    patchResults.push(patchResult);
                  }
                }
              }
            });

            await queryFulfilled;
          } catch {
            patchResults.forEach((patchResult) => {
              patchResult.undo();
            });
          }
        },
      }
    ),
    getTrackPins: builder.query<TrackPinsResponse, TrackPinsQueryParams>({
      query: (params = {}) => ({
        url: "/track_pins",
        method: "POST",
        data: params,
      }),
      transformResponse: (response: TrackPinsResponse, _meta, arg) => {
        return {
          ...response,
          camera_id: arg.camera_local_id,
          time: arg.time,
        };
      },
    }),
    loadOrderInspection: builder.query<
      OrderInspectionResponse,
      { orderId: string }
    >({
      query: ({ orderId }) => ({
        url: `/orders/${orderId}/inspect`,
        method: "POST",
      }),
    }),

    getLatestOrderInspection: builder.query<
      OrderInspectionResponse,
      { orderId: string; request_id?: string }
    >({
      query: ({ orderId, ...params }) => ({
        url: `/orders/${orderId}/latest_inspection`,
        params,
        method: "GET",
      }),
    }),

    stages: builder.query<StagesResponse, void>({
      query: () => ({ url: `/lod/stages`, method: "GET" }),
      providesTags: ["Stages"],
      keepUnusedDataFor: Infinity,
    }),

    updateOrderStageStatuses: builder.mutation<
      UpdateOrderStageStatusesResponse,
      UpdateOrderStageStatusesRequest
    >({
      query: ({ order_id, ...data }) => ({
        url: `/orders/${order_id}/stage_status`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["Order", "OrderStatus"],
    }),

    /**
     * Dashboard
     */
    dashboardStats: builder.query<
      DashboardStatsResponse,
      DashboardStatsVariables
    >({ query: (params) => ({ url: "/dashboard", method: "GET", params }) }),

    dashboardOrders: builder.query<
      DashboardOrdersResponse,
      DashboardOrdersVariables
    >({
      query: (params) => ({ url: "/dashboard/orders", method: "GET", params }),
    }),

    dashboardOrderDetails: builder.query<
      DashboardOrderDetailsResponse,
      { id: string; request_id?: string }
    >({
      query: ({ id, ...params }) => ({
        url: `/dashboard/orders/${id}`,
        method: "GET",
        params,
      }),
    }),

    dashboardZoneBasedPredictions: builder.query<
      DashboardZoneBasedPredictionResponse,
      { orderId: string }
    >({
      query: ({ orderId }) => ({
        url: `/dashboard/orders/${orderId}/zone_based_prediction`,
        method: "GET",
      }),
    }),

    dashboardPinBasedPredictions: builder.query<
      DashboardPinBasedPredictionResponse,
      { orderId: string }
    >({
      query: ({ orderId }) => ({
        url: `/dashboard/orders/${orderId}/pin_based_prediction`,
        method: "GET",
      }),
    }),

    dashboardSystemCurrentTime: builder.query<
      DashboardSystemCurrentTimeResponse,
      void
    >({
      query: () => ({
        url: `/dashboard/system_current_time_state`,
        method: "GET",
      }),
    }),

    getJourneyPredictionCart: builder.query<
      JourneyPredictionCartResponse,
      { orderTime: string; journeys: JourneyPredictionPayload[] }
    >({
      query: ({ orderTime, journeys }) => ({
        url: `/prediction/journeys_prediction_cart`,
        method: "POST",
        data: {
          order_time: orderTime,
          journeys: journeys,
        },
      }),
    }),

    reEvaluateAllSteps: builder.mutation<unknown, { order_id: string }>({
      query: ({ order_id, ...data }) => ({
        url: `/dashboard/orders/${order_id}/re_evaluate_all_steps`,
        method: "POST",
        data,
      }),
      invalidatesTags: ["DashboardOrderHistories"],
    }),

    dashboardOrderHistories: builder.query<
      DashboardOrderHistoriesResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/dashboard/orders/${id}/histories`,
        method: "GET",
      }),
      providesTags: ["DashboardOrderHistories"],
    }),

    getHandProjection: builder.query<
      HandProjectionResponse,
      HandProjectionQueryParams
    >({
      query: (params) => ({
        url: "/hand_projection",
        method: "POST",
        data: params,
      }),
    }),

    pipelineVersions: builder.query<
      PipelineVersionsResponse,
      PipelineVersionsVariables
    >({
      query: (params) => ({
        url: "/dashboard/pipeline_versions",
        method: "GET",
        data: params,
      }),
    }),
  }),
});

export const {
  useGetOrdersInfiniteInfiniteQuery,
  useGetOrdersPaginatedQuery,
  useLazyOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useCreateJourneysMutation,
  useUpdateJourneysMutation,
  useDeleteJourneysMutation,
  useCreateJourneyTransactionsMutation,
  useDeleteJourneyTransactionsMutation,
  useCreateTrackPersistencesMutation,
  useDeleteTrackPersistencesMutation,
  useCreateJourneyProximitiesMutation,
  useDeleteJourneyProximitiesMutation,
  useCreateProximityTimeRangesMutation,
  useDeleteProximityTimeRangesMutation,
  useLazyGetExportCountsQuery,
  useAssignOrdersMutation,
  useGetTrackPinsQuery,
  useLoadOrderInspectionQuery,
  useLazyLoadOrderInspectionQuery,
  useGetLatestOrderInspectionQuery,
  useLazyGetLatestOrderInspectionQuery,
  useStagesQuery,
  useUpdateOrderStageStatusesMutation,
  useDashboardZoneBasedPredictionsQuery,
  useDashboardPinBasedPredictionsQuery,
  useDashboardOrderDetailsQuery,
  useDashboardOrdersQuery,
  useDashboardStatsQuery,
  useLazyDashboardPinBasedPredictionsQuery,
  useLazyDashboardZoneBasedPredictionsQuery,
  useDashboardOrderHistoriesQuery,
  useLazyDashboardOrderHistoriesQuery,
  useReEvaluateAllStepsMutation,
  useGetHandProjectionQuery,
  useLazyGetHandProjectionQuery,
  useDashboardSystemCurrentTimeQuery,
  useGetJourneyPredictionCartQuery,
  useLazyGetJourneyPredictionCartQuery,
  usePipelineVersionsQuery,
  useLazyPipelineVersionsQuery,
} = lodApi;

export type OrdersQueryParams = {
  search?: string;
  page?: number;
  limit?: number;
  ordering?: string;
  order_source?: string;
  annotator_id?: string;
  from_datetime?: string;
  to_datetime?: string;
  stage_name?: string;
  stage_status?: string;
  stage_status_negate?: string;
};

export type OrdersResponse = { results: Order[]; count: number };

export type OrderWithJourneysResponse = { result: OrderWithJourneys };

export type OrderLine = {
  barcode: string;
  image_url: string;
  name: string;
  quantity: number;
  sku_base_id: number;
  variant_id: string;
  uom: string;
};

export type Order = {
  id: string;
  time: string;
  order_source: string;
  lines: OrderLine[];
  annotator_id: string;
  status: string;
  stage_detail_statuses?: StageDetailStatus[];
  anomaly_report?: string;
  zone?: {
    zone_2d_polygon: number[] | null;
    zone_3d_polygon: number[] | null;
    zone_id: string;
    zone_name: string;
  };
  zone_camera_id?: string | null;
};

export type JourneyEventRequest = {
  enter_camera_id: string;
  enter_pin_xy: [number, number];
  enter_time: string;
  exit_camera_id: string;
  exit_pin_xy: number[];
  exit_time: string;
  note: string;
  enter_note: string;
  exit_note: string;
  enter_camera_image_width: number;
  enter_camera_image_height: number;
  exit_camera_image_width: number;
  exit_camera_image_height: number;
  enter_zone?: {
    zone_2d_polygon: number[];
    zone_3d_polygon: number[];
    zone_id: string;
    zone_name: string;
  } | null;
  exit_zone?: {
    zone_2d_polygon: number[];
    zone_3d_polygon: number[];
    zone_id: string;
    zone_name: string;
  } | null;
};

export type JourneyPredictionPayload = {
  enter_camera_id: string;
  enter_pin_xy: [number, number];
  enter_time: string;
  enter_zone: {
    zone_2d_polygon: number[];
    zone_3d_polygon: number[];
    zone_id: string;
    zone_name: string;
  } | null;
  exit_camera_id: string;
  exit_pin_xy: [number, number];
  exit_time: string;
  exit_zone: {
    zone_2d_polygon: number[];
    zone_3d_polygon: number[];
    zone_id: string;
    zone_name: string;
  } | null;
  enter_camera_image_width: number;
  enter_camera_image_height: number;
  exit_camera_image_width: number;
  exit_camera_image_height: number;
};

export type JourneyEventResponse = { id: string } & JourneyEventRequest;

export type JourneyTransaction = {
  id: string;
  type: typeof ANNOTATION_TYPES.TRANSACTION;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  timestamp: string;
  name: string;
  barcode: string;
  imageUrl: string;
  skuId: number;
  variantId: string;
  count: number;
  uom: string;
  action: CustomerAction;
  gondolaId: number;
  gondolaName: string;
  planogramId: number;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  cameraId: number;
  calibrationId: number;
  notes: string;
};

export type JourneyTransactionRequest = {
  closest_time_point: string;

  barcode: string;
  image_url: string;
  name: string;
  sku_base_id: number;
  variant_id: string;

  count: number;
  update_type: "add" | "remove";
  gondola_id: number;
  gondola_name: string;
  planogram_id: number;

  camera_image_height: number;
  camera_image_width: number;
  item_pin_2d_xy: [number, number];

  camera_local_id: number;
  calibration_id: number;

  notes: string;
  uom: string;
};

export type JourneyTransactionResponse = {
  id: string;
} & JourneyTransactionRequest;

export type TrackPersistence = {
  id: string;
  type: typeof ANNOTATION_TYPES.TRACK_PERSISTENCE;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  timestamp: string;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  cameraId: number;
  calibrationId: number;
  notes: string;
};

export type TrackPersistenceRequest = {
  closest_time_point: string;
  camera_image_height: number;
  camera_image_width: number;
  shoulder_center2d_xy: [number, number];
  camera_local_id: number;
  calibration_id: number;
  notes: string;
};

export type TrackPersistenceResponse = { id: string } & TrackPersistenceRequest;

export type JourneyProximity = {
  id: string;
  type: typeof ANNOTATION_TYPES.PROXIMITY;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  handId: HandId;
  timestamp: string;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  cameraId: number;
  calibrationId: number;
  gondolaId: number;
  gondolaName: string;
  planogramId: number;
  notes: string;
};

export type JourneyProximityRequest = {
  closest_time_point: string;
  hand_id: number;
  camera_image_height: number;
  camera_image_width: number;
  wrist2d_xy: [number, number];
  camera_local_id: number;
  calibration_id: number;
  gondola_id: number;
  gondola_name: string;
  planogram_id: number;
  notes: string;
};

export type JourneyProximityResponse = { id: string } & JourneyProximityRequest;

export type ProximityTimeRange = {
  id: string;
  type: typeof ANNOTATION_TYPES.PROXIMITY_TIME_RANGES;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  proximityId: string;
  handId: HandId;
  timestamp: string;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  gondolaId: number;
  gondolaName: string;
  planogramId: number;
  beforeRangeStartTime: string;
  beforeRangeEndTime: string;
  afterRangeStartTime: string;
  afterRangeEndTime: string;
  cameraId: number;
  calibrationId: number;
  notes: string;
};

export type ProximityTimeRangeRequest = {
  proximity_annotation_id: string;
  before_range_start_time: string;
  before_range_end_time: string;
  after_range_start_time: string;
  after_range_end_time: string;
  camera_local_id: number;
  calibration_id: number;
  notes: string;
};

export type ProximityTimeRangeResponse = {
  id: string;
} & ProximityTimeRangeRequest;

export interface ExportCountsQueryParams {
  search?: string;
  order_source?: string;
  annotator_id?: string;
  from_datetime?: string;
  to_datetime?: string;
  ordering?: string;
  stage_journey_annotation?: string;
  stage_transaction_annotation?: string;
  stage_track_persistence_annotation?: string;
}

export interface ExportCountsResponse {
  result: {
    orders_count: number;
    journeys_count: number;
    transaction_annotations_count: number;
    proximity_annotations_count: number;
    track_persistence_annotations_count: number;
  };
}

export interface ExportAnnotationsRequest {
  from_datetime?: string;
  to_datetime?: string;
  order_source?: string;
  search?: string;
  orders: boolean;
  journeys: boolean;
  transaction_annotations: boolean;
  proximity_annotations: boolean;
  track_persistence_annotations: boolean;
}

interface AssignOrdersResponse {
  success: boolean;
}

interface AssignOrdersVariables {
  order_ids: string[];
  annotator_id: string | null;
}

export interface TrackPinsQueryParams {
  [key: string]: any;
}

export interface TrackPinsResponse {
  result: {
    pin_points_2d: [number, number][];
    track_ids: number[];
    closest_times_per_track_id: string[];
  };
  camera_id: number; // This is the camera_local_id from the request params
  time: string; // This is the time from the request params
  count?: number;
}

export interface OrderInspectionResponse {
  result: GondolaChangeGroupsData;
  count?: number;
}

export interface TrackPin {
  id: string;
  track_id: string;
  timestamp: string;
  camera_id: number;
  pin_coordinates: [number, number];
  camera_image_width: number;
  camera_image_height: number;
}
export type StageStatus = "not_started" | "done" | "rejected";

export type StageDetailStatus = {
  stage: string;
  status: StageStatus;
  rejection_reason: string | null;
};

export type RejectionReason = { code: string };

export type Stage = { code: string; rejection_reasons: RejectionReason[] };

export type StagesResponse = {
  result: { stages: Stage[]; statuses: StageStatus[] };
};

export type StageStatusRequest = {
  stage: string;
  status: StageStatus;
  rejection_reason: string | null;
};

export type UpdateOrderStageStatusesRequest = {
  order_id: string;
  stage_statuses: StageStatusRequest[];
  anomaly_report: string | null;
};

export type UpdateOrderStageStatusesResponse = { success: boolean };

export type DashboardStatsResponse = {
  results: {
    pre_order_id: string;
    order_id: string;
    requested_at: string;
    zone_based_f1_score: number;
    pin_based_f1_score: number;
    pipeline_version: string;
  }[];
  count: number;
};
type DashboardStatsVariables = {
  search?: string;
  from_datetime?: string;
  to_datetime?: string;
  n_from_latest?: number;
  order_source?: string;
  annotator_id?: string;
  stage_journey_annotation?: string;
  stage_transaction_annotation?: string;
  stage_track_persistence_annotation?: string;
  journeys_count?: string;
  sku_ids?: string;
  order_id?: string;
  pipeline_version?: string;
};

export type DashboardOrder = {
  order: {
    id: string;
    time: string;
    order_source: string;
    lines: {
      barcode: string;
      image_url: string;
      name: string;
      quantity: number;
      sku_base_id: number;
      variant_id: string;
    }[];
    annotator_id: string;
    status: "not_started";
    stage_detail_statuses: {
      stage: "journey_annotation";
      status: "not_started";
      rejection_reason: "missing_video_type_1";
    }[];
    anomaly_report: string;
    zone: {
      zone_2d_polygon: number[];
      zone_3d_polygon: number[];
      zone_id: string;
      zone_name: string;
    };
    zone_camera_id: string;
    created_at: string;
  };
  request_id: string;
  zone_based_prediction_evaluation: {
    f1_score: number;
    true_positives_count: number;
    false_positives_count: number;
    false_negatives_count: number;
    created_at: string;
  };
  pin_based_prediction_evaluation: {
    f1_score: number;
    true_positives_count: number;
    false_positives_count: number;
    false_negatives_count: number;
    created_at: string;
  };
  journey_count: number;
  elapsed_time: number;
  pipeline_version?: string;
  pipeline_description?: string;
};

type DashboardOrdersResponse = { results: DashboardOrder[]; count: number };

type DashboardOrdersVariables = {
  search?: string;
  limit: number;
  page: number;
  ordering?: string;
  from_datetime?: string;
  annotator_id?: string;
  to_datetime?: string;
  order_source?: string;
  stage_journey_annotation?: string;
  stage_transaction_annotation?: string;
  stage_track_persistence_annotation?: string;
  sku_ids?: string;
  order_id?: string;
  journeys_count?: string;
};

export type DashboardOrderDetailsResponse = {
  result: {
    order: {
      id: string;
      time: string;
      order_source: string;
      lines: {
        barcode: string;
        image_url: string;
        name: string;
        quantity: number;
        uom: string;
        sku_base_id: number;
        variant_id: string;
      }[];
      annotator_id: string;
      status: "not_started";
      stage_detail_statuses: {
        stage:
          | "journey_annotation"
          | "track_persistence_annotation"
          | "transaction_annotation";
        status: "not_started" | "done" | "rejected";
        rejection_reason:
          | "missing_video_type_1"
          | "wrong_planogram_layout"
          | "other";
      }[];
      anomaly_report: string;
      zone: {
        zone_2d_polygon: number[];
        zone_3d_polygon: number[];
        zone_id: string;
        zone_name: string;
      };
      zone_camera_id: string;
      created_at: string;
    };
    request_id: string;
    order_request_history: {
      request_id: string;
      started_at: string;
      finished_at: string;
      is_failed: boolean;
      note: string;
      pipeline_version?: string;
      pipeline_description?: string;
    };
    zone_based_prediction_cart:
      | {
          quantity: number;
          uom: string;
          sku_base_id: number;
          name: string;
          barcode: string;
          variant_id: string;
          image_url: string;
        }[]
      | null;
    pin_based_prediction_cart:
      | {
          quantity: number;
          uom: string;
          sku_base_id: number;
          name: string;
          barcode: string;
          variant_id: string;
          image_url: string;
        }[]
      | null;
    annotated_cart:
      | {
          quantity: number;
          uom: string;
          sku_base_id: number;
          name: string;
          barcode: string;
          variant_id: string;
          image_url: string;
        }[]
      | null;
    number_of_journeys: number;
    zone_based_prediction_evaluation: {
      f1_score: number;
      true_positives_count: number;
      false_positives_count: number;
      false_negatives_count: number;
      created_at: string;
    };
    pin_based_prediction_evaluation: {
      f1_score: number;
      true_positives_count: number;
      false_positives_count: number;
      false_negatives_count: number;
      created_at: string;
    };
    zone_based_anomaly: {
      indicator_group_per_track_id: {
        [track_id: string]: { [key: string]: number } & {
          track_anomaly_indicators: Record<string, number | number[]>[];
        };
      };
    };
    pin_based_anomaly: {
      indicator_group_per_journey_id: {
        [journey_id: string]: { [key: string]: number } & {
          track_anomaly_indicators: Record<string, number | number[]>[];
        };
      };
    };
    journey_snapshots:
      | {
          journey_id: string;
          request_id: string;
          request_time: string;
          enter_note: string;
          enter_camera_image_width: number;
          enter_camera_image_height: number;
          enter_camera_id: string;
          enter_pin_xy: number[];
          enter_time: string;
          enter_zone: {
            zone_2d_polygon: number[];
            zone_3d_polygon: number[];
            zone_id: string;
            zone_name: string;
          };
          exit_note: string;
          exit_camera_image_width: number;
          exit_camera_image_height: number;
          exit_camera_id: string;
          exit_pin_xy: number[];
          exit_time: string;
          exit_zone: {
            zone_2d_polygon: number[];
            zone_3d_polygon: number[];
            zone_id: string;
            zone_name: string;
          };
          note: string;
          transactions: any[];
        }[]
      | null;
    journey_snapshots_created_at: string;
    zone_based_prediction_created_at: string;
    pin_based_prediction_created_at: string;
  };
};

type DashboardZoneBasedPredictionResponse = {
  result: {
    order_id: string;
    request_id: string;
    type: string;
    request_time: string;
    updates: {
      [key: string]: {
        name: string;
        barcode: string;
        image_url: string;
        variant_id: string;
        sku_base_id: number;
        update_time: string;
        update_type: "add";
        update_quantity: number;
      }[];
    };
  };
};

type DashboardPinBasedPredictionResponse = {
  result: {
    order_id: string;
    request_id: string;
    type: string;
    request_time: string;
    updates: {
      [key: string]: {
        name: string;
        barcode: string;
        image_url: string;
        variant_id: string;
        sku_base_id: number;
        update_time: string;
        update_type: "add";
        update_quantity: number;
      }[];
    };
  };
};

export type DashboardOrderHistoriesResponse = {
  results: {
    request_id: string;
    started_at: string;
    finished_at: string;
    is_failed: boolean;
    note: string;
  }[];
  count: number;
};
export interface HandProjectionQueryParams {
  track_id: number;
  from_datetime: string;
  to_datetime: string;
  camera_id: string;
  hand_id: number;
}

export type HandProjectionPoint = [number, number, number]; // [x, y, confidence]

export interface HandProjectionData {
  projection: [HandProjectionPoint, HandProjectionPoint, HandProjectionPoint]; // [shoulder, elbow, wrist]
  track_time: string;
  gondola_distance?: number;
}

export interface HandProjectionResponse {
  results: HandProjectionData[];
  count: number;
}

export type DashboardSystemCurrentTimeResponse = {
  result: {
    system_datetime: string;
    latest_track_datetime: string;
    change_occlusion_finder_current_datetime: string;
    proximity_current_datetime: string;
    change_occlusion_interval_current_datetime: string;
    change_inference_current_datetime: string;
  };
};

export type JourneyPredictionCartResponse = {
  result: {
    lines: OrderLine[];
  };
};

export interface ServiceError {
  type: string;
  service: string;
  errors: Array<{
    loc: unknown[];
    msg: string;
  }>;
  python_exc?: string;
}

export interface ErrorData {
  data?: ServiceError;
}

export type PipelineVersionsResponse = {
  results: string[];
  count: number;
};

export type PipelineVersionsVariables = void;
